import {
  NOTIFY_CREDIT_BID,
  NOTIFY_RESERVE,
  SIMILAR_PROPERTY_SOURCE,
  TRANSIENT_MESSAGE_NOTIFY_CREDIT_BID_BODY,
  TRANSIENT_MESSAGE_NOTIFY_RESERVE_BODY,
  TRANSIENT_MESSAGE_PROPERTY_SAVED_BODY,
  TRANSIENT_MESSAGE_PROPERTY_SAVED_TITLE,
  TRANSIENT_MESSAGE_PROPERTY_UNSAVED_BODY,
  TRANSIENT_MESSAGE_PROPERTY_UNSAVED_TITLE,
} from 'common/statics'
import * as blueprint from '@adc/blueprint'
import { Listing, Property } from '@adc/sdk/types'
import { getApplicationNotificationsContext } from 'common/utils/applicationNotificationsBridge'
import logger from '@adc/logger'
import analytics from 'common/services/analytics'
import { AuctionUser } from '@adc/foundation'

export type ToggleSavePropertyArgs = {
  listing: Listing
  property: Property
  source?: string
  justLoggedIn?: boolean
  pushAuthQueueItem: (args: Array<any>) => void
  toggleAuthModal: () => void
  pushOnboardingQueueItem?: (args: Array<any>) => void
  nudgeOptInModal?: (() => void) | null
}

const handleSavePropertyAnalytics = ({
  listing,
  source,
  save = true,
}: {
  listing: Listing
  source: string
  save: boolean
}) => {
  if (source === SIMILAR_PROPERTY_SOURCE) {
    analytics.property.saveSimilarProperty(listing, save)
  } else {
    analytics.property[save ? 'save' : 'removeSaved'](listing)
  }
}

type SaveUnsaveArgs = Omit<
  ToggleSavePropertyArgs,
  'justLoggedIn' | 'pushAuthQueueItem' | 'toggleAuthModal'
> & {
  updateSaveProperty: ReturnType<
    typeof blueprint.user.hooks.useSavedProperties
  >['actions']['updateSaveProperty']
}

const saveProperty = ({
  updateSaveProperty,
  listing,
  property,
  source = '',
  nudgeOptInModal = null,
}: SaveUnsaveArgs) => {
  handleSavePropertyAnalytics({ listing, source, save: true })

  const notifications = getApplicationNotificationsContext()

  return updateSaveProperty(property.property_id, true)
    .then(() => {
      let message = TRANSIENT_MESSAGE_PROPERTY_SAVED_BODY

      if (source === NOTIFY_CREDIT_BID) {
        message = TRANSIENT_MESSAGE_NOTIFY_CREDIT_BID_BODY
      } else if (source === NOTIFY_RESERVE) {
        message = TRANSIENT_MESSAGE_NOTIFY_RESERVE_BODY
      }

      notifications.overlayBanner.open({
        title: TRANSIENT_MESSAGE_PROPERTY_SAVED_TITLE,
        message,
      })

      if (typeof nudgeOptInModal === 'function') {
        nudgeOptInModal()
      }
    })
    .catch(err => {
      logger.error(err)
    })
}

const unsaveProperty = ({
  updateSaveProperty,
  listing,
  property,
  source = '',
}: SaveUnsaveArgs) => {
  handleSavePropertyAnalytics({ listing, source, save: false })

  const notifications = getApplicationNotificationsContext()

  return updateSaveProperty(property.property_id, false)
    .then(() => {
      notifications.overlayBanner.open({
        title: TRANSIENT_MESSAGE_PROPERTY_UNSAVED_TITLE,
        message: TRANSIENT_MESSAGE_PROPERTY_UNSAVED_BODY,
      })
    })
    .catch(err => {
      logger.error(err)
    })
}

export const toggleSaveProperty =
  (
    updateSaveProperty: ReturnType<
      typeof blueprint.user.hooks.useSavedProperties
    >['actions']['updateSaveProperty'],
    user?: AuctionUser
  ) =>
  (
    {
      listing,
      property,
      source,
      justLoggedIn = false,
      pushAuthQueueItem,
      toggleAuthModal,
      pushOnboardingQueueItem,
      nudgeOptInModal,
    }: ToggleSavePropertyArgs,
    _authType?: any,
    authUser?: any
  ) => {
    const isLoggedIn = user?.logged_in
    if (justLoggedIn && property.is_currently_saved) {
      return
    }

    if (isLoggedIn || authUser) {
      ;(property.is_currently_saved ? unsaveProperty : saveProperty)({
        updateSaveProperty,
        listing,
        property,
        source,
        nudgeOptInModal,
      })
    } else {
      // since user is not authenticated we should not display the opt in modal automatically but rather when the auth flow has completed
      const args = {
        listing,
        property,
        source,
        justLoggedIn: true,
        pushAuthQueueItem,
        toggleAuthModal,
      }
      pushAuthQueueItem &&
        pushAuthQueueItem([
          toggleSaveProperty(updateSaveProperty, user),
          [args],
        ])
      if (typeof nudgeOptInModal === 'function' && pushOnboardingQueueItem) {
        pushOnboardingQueueItem([nudgeOptInModal, []])
      }
      toggleAuthModal && toggleAuthModal()
    }
  }
